import type { Category, CategoryId, Guide } from "./types";
import { gettingStartedGuides } from "./guides/gettingStarted";
import { screensGuides } from "./guides/screens";
import { contentGuides } from "./guides/content";
import { modulesGuides } from "./guides/modules";
import { appsGuides } from "./guides/apps";
import { accountGuides } from "./guides/account";
import { developerGuides } from "./guides/developers";

export const CATEGORIES: Category[] = [
  {
    id: "getting-started",
    label: "Getting Started",
    description: "New here? Start with a tour and publish your first content.",
    icon: "Rocket",
    accent: "#6d5cf0",
  },
  {
    id: "screens",
    label: "Screens",
    description: "Pair displays, assign content, monitor the Wall View, and set up screen fit and video walls.",
    icon: "MonitorPlay",
    accent: "#2f8fe0",
  },
  {
    id: "content",
    label: "Content",
    description: "Media, subtitles, playlists, multi-zone channels, and the design Canvas.",
    icon: "LayoutGrid",
    accent: "#12b886",
  },
  {
    id: "modules",
    label: "Modules",
    description: "Emergency alerts and feature suites — Events, Queue, Wayfinding.",
    icon: "Boxes",
    accent: "#e08a1e",
  },
  {
    id: "apps",
    label: "Apps",
    description: "Live content apps — weather, clocks, menus, directories, and more.",
    icon: "AppWindow",
    accent: "#e2528c",
  },
  {
    id: "account",
    label: "Account & Settings",
    description: "Profile, people & roles, plan & licenses, screen settings, SSO, developer tools, downloads, and audit.",
    icon: "Settings",
    accent: "#8a8f98",
  },
  {
    id: "developers",
    label: "API & Integrations",
    description: "The REST API and webhooks: connect a building-management system, dashboards and your own tools.",
    icon: "Braces",
    accent: "#0e7490",
  },
];

const HIDDEN_MODULE_GUIDES = new Set(["smart-ads", "fleet-management"]);

export const GUIDES: Guide[] = [
  ...gettingStartedGuides,
  ...screensGuides,
  ...contentGuides,
  // Smart Ads and Fleet are hidden in the app (PR #184); keep their guides out of the site.
  ...modulesGuides.filter((g) => !HIDDEN_MODULE_GUIDES.has(g.slug)),
  ...appsGuides,
  ...accountGuides,
  ...developerGuides,
];

export function categoryById(id: CategoryId): Category | undefined {
  return CATEGORIES.find((c) => c.id === id);
}

export function guidesByCategory(id: CategoryId): Guide[] {
  return GUIDES.filter((g) => g.category === id);
}

export function guideByRoute(route: string): Guide | undefined {
  return GUIDES.find((g) => g.route === route);
}

/** Ordered list of group labels present in a category (in first-seen order). */
export function groupsForCategory(id: CategoryId): string[] {
  const seen: string[] = [];
  for (const g of guidesByCategory(id)) {
    if (g.group && !seen.includes(g.group)) seen.push(g.group);
  }
  return seen;
}

/** Naive full-text search across title, summary, keywords, and step text. */
export function searchGuides(query: string): Guide[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const terms = q.split(/\s+/);
  const scored = GUIDES.map((g) => {
    const haystack = [
      g.title,
      g.summary,
      g.intro ?? "",
      (g.keywords ?? []).join(" "),
      g.steps.map((s) => s.title + " " + s.blocks.map(blockText).join(" ")).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    let score = 0;
    for (const t of terms) {
      if (!haystack.includes(t)) return { g, score: 0 };
      // Title / summary hits weigh more than deep body hits.
      if (g.title.toLowerCase().includes(t)) score += 5;
      else if (g.summary.toLowerCase().includes(t)) score += 3;
      else score += 1;
    }
    return { g, score };
  });
  return scored
    .filter((s) => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .map((s) => s.g);
}

function blockText(b: Guide["steps"][number]["blocks"][number]): string {
  switch (b.type) {
    case "p":
    case "subheading":
    case "note":
    case "steps-ref":
      return "text" in b ? (b as { text?: string }).text ?? "" : (b as { label?: string }).label ?? "";
    case "ul":
    case "ol":
      return b.items.join(" ");
    case "table":
      return [...b.head, ...b.rows.flat()].join(" ");
    case "code":
      return [b.title ?? "", b.code].join(" ");
    default:
      return "";
  }
}
